import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { EnvelopeSimple, Plus, PencilSimple, Trash, Paperclip, FolderOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const DOC_CATEGORIES = ['Fattura', 'Comunicazione', 'Report', 'Modello Fiscale', 'Contratto', 'Altro'];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

export function EmailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '', has_estratto_conto: false, has_iscrizione_attachments: false, target_type: 'alunni', attach_doc_categories: [], source_year: '', target_year: '' });
  const [files, setFiles] = useState([]);
  const [removeAttachmentIds, setRemoveAttachmentIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.getEmailTemplates(); setTemplates(res.data); }
    catch { toast.error('Errore caricamento modelli'); }
    finally { setLoading(false); }
  }, []);

  // Auto-seed dei template di sistema (Invito Prenotazione, Conferma Prenotazione Accettata):
  // se ne cancelli uno per errore, riaprire questa sezione lo ricrea.
  useEffect(() => {
    (async () => {
      try { await api.seedSystemTemplates(); } catch { /* ignore */ }
      load();
    })();
  }, [load]);

  const handleRestoreSystem = async () => {
    if (!window.confirm('Ripristinare i template di sistema mancanti (Invito Prenotazione, Conferma Prenotazione Accettata)?\nI template già esistenti NON vengono modificati.')) return;
    try {
      const res = await api.seedSystemTemplates();
      const created = res.data.created || [];
      if (created.length) toast.success(`Ricreati: ${created.join(', ')}`);
      else toast.info('Tutti i template di sistema sono già presenti');
      load();
    } catch { toast.error('Errore ripristino template di sistema'); }
  };

  const toggleDocCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      attach_doc_categories: prev.attach_doc_categories.includes(cat)
        ? prev.attach_doc_categories.filter(c => c !== cat)
        : [...prev.attach_doc_categories, cat]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('subject', form.subject);
    formData.append('body', form.body);
    formData.append('has_estratto_conto', form.has_estratto_conto);
    formData.append('has_iscrizione_attachments', form.has_iscrizione_attachments);
    formData.append('target_type', form.target_type);
    formData.append('attach_doc_categories', form.attach_doc_categories.join(','));
    formData.append('source_year', form.source_year || '');
    formData.append('target_year', form.target_year || '');
    files.forEach(f => formData.append('files', f));
    if (removeAttachmentIds.length > 0) formData.append('remove_attachments', removeAttachmentIds.join(','));
    try {
      if (editing) { await api.updateEmailTemplate(editing.id, formData); toast.success('Modello aggiornato'); }
      else { await api.createEmailTemplate(formData); toast.success('Modello creato'); }
      setShowModal(false); load();
    } catch { toast.error('Errore salvataggio modello'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo modello?')) return;
    try { await api.deleteEmailTemplate(id); toast.success('Modello eliminato'); load(); }
    catch { toast.error('Errore eliminazione'); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', subject: '', body: '', has_estratto_conto: false, has_iscrizione_attachments: false, target_type: 'alunni', attach_doc_categories: [], source_year: '', target_year: '' });
    setFiles([]);
    setRemoveAttachmentIds([]);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, subject: t.subject, body: t.body, has_estratto_conto: t.has_estratto_conto, has_iscrizione_attachments: t.has_iscrizione_attachments || false, target_type: t.target_type || 'alunni', attach_doc_categories: t.attach_doc_categories || [], source_year: t.source_year || '', target_year: t.target_year || '' });
    setFiles([]);
    setRemoveAttachmentIds([]);
    setShowModal(true);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 animate-fade-in">
        <div><h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Modelli Email</h1><p className="text-[#52525B] text-sm">Gestisci i modelli email per alunni e insegnanti</p></div>
        <div className="flex items-center gap-2">
          <Button onClick={handleRestoreSystem} variant="outline" className="rounded-sm" data-testid="restore-system-templates-btn" title="Ricrea i template di sistema (Invito Prenotazione, Conferma Prenotazione Accettata) se sono stati cancellati">
            <FolderOpen size={16} className="mr-1" /> Ripristina Sistema
          </Button>
          <Button onClick={openNew} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] rounded-sm" data-testid="new-template-btn"><Plus size={16} className="mr-1" /> Nuovo Modello</Button>
        </div>
      </div>
      <div className="space-y-3 animate-fade-in animate-delay-1">
        {loading ? <div className="text-center py-12 text-[#52525B]">Caricamento...</div> : templates.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm"><EnvelopeSimple size={48} className="mx-auto text-[#E4E4E7] mb-4" /><p className="text-[#52525B]">Nessun modello email. Crea il primo!</p></div>
        ) : templates.map(t => (
          <div key={t.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold font-['Work_Sans']">{t.name}</h3>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.target_type === 'insegnanti' ? 'bg-blue-100 text-blue-700' : t.target_type === 'potenziali' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>{t.target_type === 'insegnanti' ? 'Insegnanti' : t.target_type === 'potenziali' ? 'Potenziali' : 'Alunni'}</span>
                  {t.has_estratto_conto && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Estratto Conto</span>}
                  {t.has_iscrizione_attachments && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Reg + Listino</span>}
                  {t.attach_doc_categories?.length > 0 && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">Doc: {t.attach_doc_categories.join(', ')}</span>}
                </div>
                <p className="text-sm text-[#52525B] mb-1 break-words"><strong>Oggetto:</strong> {t.subject}</p>
                <div
                  className="text-sm text-[#71717A] line-clamp-3 prose prose-sm max-w-none break-words [&_p]:m-0 [&_p+p]:mt-1 [&_ul]:my-0 [&_ol]:my-0 [&_a]:break-all"
                  dangerouslySetInnerHTML={{ __html: t.body || '' }}
                />
                {t.attachments?.length > 0 && <div className="flex items-center gap-1 mt-2 text-xs text-[#52525B]"><Paperclip size={12} />{t.attachments.length} allegat{t.attachments.length === 1 ? 'o' : 'i'}: {t.attachments.map(a => a.filename).join(', ')}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-[#F4F4F5] text-[#52525B]" data-testid={`template-edit-${t.id}`} title="Modifica modello"><PencilSimple size={16} /></button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`template-delete-${t.id}`} title="Elimina modello"><Trash size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Work_Sans']">{editing ? 'Modifica Modello' : 'Nuovo Modello Email'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2"><Label className="label-overline">Destinatari</Label><div className="flex gap-2"><Button type="button" variant={form.target_type === 'alunni' ? 'default' : 'outline'} size="sm" onClick={() => setForm({...form, target_type: 'alunni'})} className="rounded-sm" data-testid="template-type-alunni">Alunni</Button><Button type="button" variant={form.target_type === 'insegnanti' ? 'default' : 'outline'} size="sm" onClick={() => setForm({...form, target_type: 'insegnanti', has_estratto_conto: false})} className="rounded-sm" data-testid="template-type-insegnanti">Insegnanti</Button><Button type="button" variant={form.target_type === 'potenziali' ? 'default' : 'outline'} size="sm" onClick={() => setForm({...form, target_type: 'potenziali', has_estratto_conto: false})} className="rounded-sm" data-testid="template-type-potenziali">Alunni Potenziali</Button></div></div>
            <div className="space-y-2"><Label className="label-overline">Nome modello</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Es. Circolare inizio anno" required className="rounded-sm" data-testid="template-name-input" /></div>
            <div className="space-y-2"><Label className="label-overline">Oggetto email</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Es. Comunicazione scuola di musica" required className="rounded-sm" data-testid="template-subject-input" /></div>
            <div className="space-y-2"><Label className="label-overline">Testo email</Label>
              <div className="border border-[#E4E4E7] rounded-sm bg-white" data-testid="template-body-input">
                <ReactQuill
                  value={form.body}
                  onChange={(v) => setForm({...form, body: v})}
                  modules={QUILL_MODULES}
                  theme="snow"
                  placeholder="Scrivi il testo della mail..."
                  style={{ minHeight: 250 }}
                />
              </div>
              <details className="text-[10px] text-[#A1A1AA]">
                <summary className="cursor-pointer hover:text-[#52525B]">Variabili disponibili (clicca per espandere)</summary>
                <div className="mt-1 p-2 bg-[#F4F4F5] rounded-sm grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
                  {form.target_type === 'alunni' && <><span>{'{{nome_alunno}}'}</span><span>{'{{codice_alunno}}'}</span><span>{'{{telefono_alunno}}'}</span><span>{'{{email_alunno}}'}</span><span>{'{{riepilogo_addebiti}}'}</span><span>{'{{riepilogo_lezioni}}'}</span><span>{'{{riepilogo_iscrizioni}}'}</span><span>{'{{tariffe_per_lezione}}'}</span><span>{'{{stato_pagamenti}}'}</span><span>{'{{estratto_conto}}'}</span><span>{'{{anno_corrente}}'}</span><span>{'{{anno_target}}'}</span><span>{'{{scadenza_1}}'}</span><span>{'{{scadenza_2}}'}</span><span>{'{{scadenza_3}}'}</span><span>{'{{iban_azienda}}'}</span><span>{'{{email_azienda}}'}</span><span>{'{{conferma_lezione_link}}'}</span><span>{'{{link_prenotazione}}'}</span><span>{'{{tariffa_applicata}}'}</span></>}
                  {form.target_type === 'insegnanti' && <><span>{'{{nome_insegnante}}'}</span><span>{'{{codice_insegnante}}'}</span><span>{'{{telefono_insegnante}}'}</span><span>{'{{email_insegnante}}'}</span><span>{'{{strumento}}'}</span><span>{'{{username}}'}</span><span>{'{{password}}'}</span><span>{'{{ruolo}}'}</span></>}
                  {form.target_type === 'potenziali' && <><span>{'{{nome_alunno}}'}</span><span>{'{{telefono_alunno}}'}</span><span>{'{{email_alunno}}'}</span></>}
                  <span>{'{{nome_azienda}}'}</span><span>{'{{telefono_azienda}}'}</span>
                </div>
              </details>
            </div>
            {form.target_type === 'alunni' && <div className="flex items-center gap-2"><input type="checkbox" id="estratto_conto" checked={form.has_estratto_conto} onChange={e => setForm({...form, has_estratto_conto: e.target.checked})} className="rounded" data-testid="template-estratto-conto-check" /><Label htmlFor="estratto_conto" className="text-sm cursor-pointer">Allega Estratto Conto (PDF addebiti alunno)</Label></div>}
            {form.target_type === 'alunni' && <div className="flex items-center gap-2"><input type="checkbox" id="iscrizione_attachments" checked={form.has_iscrizione_attachments} onChange={e => setForm({...form, has_iscrizione_attachments: e.target.checked})} className="rounded" data-testid="template-iscrizione-attachments-check" /><Label htmlFor="iscrizione_attachments" className="text-sm cursor-pointer">Allega Regolamento + Listino Prezzi (URL da settaggi Azienda)</Label></div>}

            {form.target_type === 'alunni' && (
              <div className="space-y-2 border-t border-[#E4E4E7] pt-3">
                <Label className="label-overline">Conferma Iscrizione (opzionale)</Label>
                <p className="text-[10px] text-[#A1A1AA]">Compila i due anni per abilitare le variabili <code>{'{{conferma_lezione_link}}'}</code>, <code>{'{{anno_corrente}}'}</code>, <code>{'{{anno_target}}'}</code>, <code>{'{{riepilogo_lezioni}}'}</code>. La variabile <code>{'{{tariffa_applicata}}'}</code> verrà popolata in base al listino scelto al momento dell&apos;invio.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#52525B]">Anno sorgente (es. 2025)</Label>
                    <Input type="number" value={form.source_year} onChange={e => setForm({...form, source_year: e.target.value})} placeholder="2025" className="rounded-sm" data-testid="template-source-year" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#52525B]">Anno destinazione (es. 2026)</Label>
                    <Input type="number" value={form.target_year} onChange={e => setForm({...form, target_year: e.target.value})} placeholder="2026" className="rounded-sm" data-testid="template-target-year" />
                  </div>
                </div>
              </div>
            )}

            {form.target_type === 'insegnanti' && (
              <div className="space-y-2">
                <Label className="label-overline flex items-center gap-1"><FolderOpen size={14} /> Allega documenti utente per categoria</Label>
                <p className="text-[10px] text-[#A1A1AA]">I documenti del destinatario nelle categorie selezionate verranno allegati automaticamente</p>
                <div className="flex flex-wrap gap-2">
                  {DOC_CATEGORIES.map(cat => (
                    <button key={cat} type="button" onClick={() => toggleDocCategory(cat)}
                      className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${form.attach_doc_categories.includes(cat) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'}`}
                      data-testid={`doc-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
                    >{cat}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2"><Label className="label-overline">Allegati fissi</Label>{editing?.attachments?.length > 0 && <div className="space-y-1 mb-2">{editing.attachments.filter(a => !removeAttachmentIds.includes(a.id)).map(a => <div key={a.id} className="flex items-center gap-2 text-xs bg-[#F4F4F5] px-2 py-1 rounded"><Paperclip size={12} /><span className="flex-1">{a.filename}</span><button type="button" onClick={() => setRemoveAttachmentIds(prev => [...prev, a.id])} className="text-red-500 hover:text-red-700 font-bold ml-1" data-testid={`remove-attachment-${a.id}`} title="Rimuovi allegato">&times;</button></div>)}</div>}<input type="file" multiple onChange={e => setFiles(Array.from(e.target.files))} className="text-sm" data-testid="template-files-input" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setShowModal(false)} className="rounded-sm">Annulla</Button><Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="template-save-btn">{editing ? 'Aggiorna' : 'Crea'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
